// MyLibrarySceneDOM.js - Beautiful Library for Philosophy Flow Saved Content
// Matches the modern template theme used in Analytics, Hall of Fame, etc.

import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db, firebase } from "../firebaseConfig.js";
import { createSidebarNavigation } from "../components/SharedSidebarNavigation.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

export default class MyLibrarySceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "MyLibrarySceneDOM" });
        this.domElement = null;
        this.savedQuotes = [];
        this.currentFilter = 'all'; // all, favorited, recent
        this.selectedFilterTags = []; // Tags to filter by
        this.allTags = []; // All unique tags from user's quotes
    }

    async create() {
        console.log('📚 MyLibrarySceneDOM: Creating beautiful library interface');
        
        // Load saved Philosophy Flow quotes
        await this.loadSavedQuotes();
        
        // Create the interface
        this.createLibraryInterface();
        
        // Load CSS
        this.loadLibraryCSS();
        
        // Initialize with enterprise services
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true
        });
        
        // Setup library-specific event listeners
        this.setupEventListeners();
        
        this.sceneReady = true;
    }

    async loadSavedQuotes() {
        try {
            const user = auth.currentUser;
            if (!user) {
                this.savedQuotes = [];
                return;
            }

            console.log('📚 Loading saved quotes from Firebase for user:', user.uid);
            
            // Load from philosophyQuotes collection
            const quotesSnapshot = await db.collection('philosophyQuotes')
                .where('userId', '==', user.uid)
                .orderBy('createdAt', 'desc')
                .limit(100)
                .get();
            
            this.savedQuotes = [];
            const tagsSet = new Set();
            
            quotesSnapshot.forEach(doc => {
                const data = doc.data();
                console.log(`📚 Loading quote ${doc.id}:`, {
                    quote: data.quote?.substring(0, 50) + '...',
                    tags: data.tags,
                    executiveNote: data.executiveNote?.substring(0, 30) + '...'
                });
                
                this.savedQuotes.push({
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate()
                });
                
                // Collect all unique tags
                if (data.tags && Array.isArray(data.tags)) {
                    console.log(`🏷️ Found tags for quote ${doc.id}:`, data.tags);
                    data.tags.forEach(tag => tagsSet.add(tag));
                } else {
                    console.log(`⚠️ No tags found for quote ${doc.id}`);
                }
            });
            
            this.allTags = Array.from(tagsSet).sort();
            console.log(`📚 Loaded ${this.savedQuotes.length} saved quotes with ${this.allTags.length} unique tags`);
            console.log('🏷️ All unique tags:', this.allTags);
            
        } catch (error) {
            console.error('Error loading saved quotes:', error);
            this.savedQuotes = [];
            this.allTags = [];
        }
    }

    createLibraryInterface() {
        // Add critical CSS
        addCriticalNavigationCSS();
        
        // Remove existing overlay
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        overlay.innerHTML = this.createLibraryHTML();

        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        // Register with overlay manager
        templateOverlayManager.registerOverlay('auth-menu-dom-overlay', 'MyLibrarySceneDOM');
    }

    createLibraryHTML() {
        const user = auth.currentUser;
        const initials = user?.email ? user.email.substring(0, 2).toUpperCase() : 'EX';
        const userName = user?.displayName || user?.email || 'Executive';
        
        // Use shared sidebar component for 100% consistency
        const sidebar = createSidebarNavigation({
            activeScene: 'my-library',
            userInitials: initials,
            userName: userName,
            userRole: 'Executive'
        });
        
        return `
            <!-- Animated Background -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- App Container -->
            <div class="app-container">
                <!-- Sidebar Navigation (Shared Component - Single Source of Truth) -->
                ${sidebar}
                
                <!-- Main Content -->
                <main class="main-content">
                    <!-- Header -->
                    <header class="header">
                        <div class="header-left">
                            <h1 class="page-title">My Library</h1>
                            <div class="breadcrumb">
                                <span>Home</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Library</span>
                            </div>
                        </div>
                        <div class="header-right">
                            <div class="filter-controls" style="display: flex; gap: 16px; align-items: center;">
                                <div class="filter-tabs">
                                    <button class="filter-tab active" data-filter="all">All (${this.savedQuotes.length})</button>
                                    <button class="filter-tab" data-filter="favorited">Favorited</button>
                                    <button class="filter-tab" data-filter="recent">Recent</button>
                                </div>
                                <div class="tag-filter-dropdown" style="position: relative;">
                                    <button class="tag-filter-btn" id="tagFilterBtn" style="
                                        padding: 8px 16px;
                                        background: rgba(168, 85, 247, 0.1);
                                        border: 1px solid rgba(168, 85, 247, 0.3);
                                        border-radius: 8px;
                                        color: #a855f7;
                                        font-size: 14px;
                                        cursor: pointer;
                                        display: flex;
                                        align-items: center;
                                        gap: 6px;
                                    ">
                                        🏷️ Filter by Tag ${this.allTags.length > 0 ? `(${this.allTags.length})` : ''}
                                        <span style="opacity: 0.7;">▼</span>
                                    </button>
                                    <div id="tagFilterDropdown" class="tag-filter-dropdown-menu" style="
                                        position: absolute;
                                        top: 100%;
                                        right: 0;
                                        margin-top: 8px;
                                        background: rgba(30, 41, 59, 0.95);
                                        border: 1px solid rgba(168, 85, 247, 0.3);
                                        border-radius: 12px;
                                        padding: 12px;
                                        min-width: 220px;
                                        max-height: 300px;
                                        overflow-y: auto;
                                        display: none;
                                        z-index: 1000;
                                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
                                    ">
                                        ${this.allTags.length > 0 ? this.allTags.map(tag => {
                                            const count = this.savedQuotes.filter(q => q.tags && q.tags.includes(tag)).length;
                                            return `
                                                <div class="tag-filter-option" data-tag="${tag}" style="
                                                    padding: 10px 12px;
                                                    cursor: pointer;
                                                    border-radius: 6px;
                                                    display: flex;
                                                    justify-content: space-between;
                                                    align-items: center;
                                                    margin-bottom: 4px;
                                                    transition: background 0.2s ease;
                                                " onmouseover="this.style.background='rgba(168, 85, 247, 0.1)'" onmouseout="this.style.background='transparent'">
                                                    <span style="color: rgba(255, 255, 255, 0.9); font-size: 14px;">🏷️ ${tag}</span>
                                                    <span style="color: rgba(255, 255, 255, 0.5); font-size: 12px;">(${count})</span>
                                                </div>
                                            `;
                                        }).join('') : `
                                            <div style="padding: 16px; text-align: center; color: rgba(255, 255, 255, 0.6); font-size: 13px;">
                                                No tags yet<br>
                                                <span style="font-size: 11px; opacity: 0.8;">Add tags when saving quotes</span>
                                            </div>
                                        `}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>
                    
                    <!-- Library Content -->
                    <div class="dashboard-content">
                        ${this.createQuotesGridHTML()}
                    </div>
                </main>
            </div>
        `;
    }

    createQuotesGridHTML() {
        if (this.savedQuotes.length === 0) {
            return `
                <div class="empty-state">
                    <div class="empty-state-icon">🎭</div>
                    <h2 class="empty-state-title">Your Library is Empty</h2>
                    <p class="empty-state-description">Save quotes from Philosophy Flow to build your collection of wisdom and insights.</p>
                    <button class="btn-primary" data-game="philosophy-flow" style="
                        margin-top: 24px;
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        color: #0f172a;
                        padding: 12px 32px;
                        border-radius: 10px;
                        font-size: 16px;
                        font-weight: 600;
                        cursor: pointer;
                    ">✨ Start Philosophy Flow</button>
                </div>
            `;
        }

        return `
            <div class="quotes-grid">
                ${this.createQuoteCardsHTML(this.savedQuotes)}
            </div>
        `;
    }
    
    createQuoteCardsHTML_OLD(quotes) {
        return quotes.map((quote, index) => {
            const dateStr = quote.createdAt ? quote.createdAt.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            }) : 'Recently';
            
            const favoriteIcon = quote.favorited ? '⭐' : '☆';
            
            return `
                <div class="quote-card" data-quote-id="${quote.id}">
                    <div class="quote-header">
                        <div class="quote-meta">
                            <span class="quote-date">📅 ${dateStr}${quote.createdAt ? ` at ${quote.createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                        </div>
                        <button class="favorite-btn ${quote.favorited ? 'active' : ''}" data-quote-id="${quote.id}">
                            ${favoriteIcon}
                        </button>
                    </div>
                    
                    <!-- Session Context from Philosophy Flow -->
                    ${(quote.selectedCategory || quote.selectedEmotion || quote.selectedColor || quote.selectedSleep) ? `
                        <div class="quote-metadata" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; padding: 12px; background: rgba(255, 255, 255, 0.02); border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.05);">
                            <div style="font-size: 11px; color: rgba(255, 255, 255, 0.5); width: 100%; margin-bottom: 6px; font-weight: 600;">Session Context:</div>
                            ${quote.selectedCategory ? `
                                <span class="meta-badge" style="
                                    background: rgba(16, 185, 129, 0.15);
                                    border: 1px solid rgba(16, 185, 129, 0.3);
                                    color: #10b981;
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">${quote.selectedCategory.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            ` : ''}
                            ${quote.selectedEmotion && quote.selectedEmotion.emoji && quote.selectedEmotion.label ? `
                                <span class="meta-badge" style="
                                    background: rgba(0, 212, 255, 0.15);
                                    border: 1px solid rgba(0, 212, 255, 0.3);
                                    color: #00d4ff;
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">${quote.selectedEmotion.emoji} ${quote.selectedEmotion.label}</span>
                            ` : ''}
                            ${quote.selectedColor && quote.selectedColor.name ? `
                                <span class="meta-badge" style="
                                    background: ${quote.selectedColor.hex}22;
                                    border: 1px solid ${quote.selectedColor.hex}66;
                                    color: ${quote.selectedColor.hex};
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">${quote.selectedColor.name}</span>
                            ` : ''}
                            ${quote.selectedSleep ? `
                                <span class="meta-badge" style="
                                    background: rgba(139, 92, 246, 0.15);
                                    border: 1px solid rgba(139, 92, 246, 0.3);
                                    color: #8b5cf6;
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">💤 ${quote.selectedSleep.hours || quote.selectedSleep} hrs</span>
                            ` : ''}
                        </div>
                    ` : ''}
                    
                    <div class="quote-text">
                        "${quote.quote || 'No quote text'}"
                    </div>
                    
                    <div class="quote-author">
                        — ${quote.figure || quote.author || 'Unknown'}
                    </div>
                    
                    ${quote.reasoning || quote.context ? `
                        <div class="context-section" style="margin-top: 16px;">
                            <button class="context-toggle-btn" data-quote-id="${quote.id}" onclick="this.classList.toggle('expanded'); const content = this.nextElementSibling; const arrow = this.querySelector('.toggle-arrow'); if(content.style.display === 'none' || !content.style.display) { content.style.display = 'block'; arrow.textContent = '▲'; } else { content.style.display = 'none'; arrow.textContent = '▼'; }" style="
                                width: 100%;
                                text-align: left;
                                background: rgba(0, 212, 255, 0.05);
                                border: 1px solid rgba(0, 212, 255, 0.2);
                                border-radius: 8px;
                                padding: 10px 12px;
                                color: #00d4ff;
                                font-size: 13px;
                                font-weight: 600;
                                cursor: pointer;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                transition: all 0.3s ease;
                            " onmouseover="this.style.background='rgba(0, 212, 255, 0.1)'; this.style.borderColor='rgba(0, 212, 255, 0.4)'" onmouseout="this.style.background='rgba(0, 212, 255, 0.05)'; this.style.borderColor='rgba(0, 212, 255, 0.2)'">
                                <span>📖 View Context & Reasoning</span>
                                <span class="toggle-arrow">▼</span>
                            </button>
                            <div class="context-content" style="
                                display: none;
                                margin-top: 8px;
                                padding: 12px;
                                background: rgba(0, 212, 255, 0.05);
                                border-left: 3px solid #00d4ff;
                                border-radius: 8px;
                                font-size: 14px;
                                line-height: 1.6;
                                color: rgba(255, 255, 255, 0.8);
                            ">
                                ${quote.reasoning || quote.context}
                            </div>
                        </div>
                    ` : ''}
                    
                    ${quote.tags && quote.tags.length > 0 ? `
                        <div class="quote-tags" style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px;">
                            ${quote.tags.map(tag => `
                                <span class="tag-badge" style="
                                    background: rgba(168, 85, 247, 0.15);
                                    border: 1px solid rgba(168, 85, 247, 0.3);
                                    color: #a855f7;
                                    padding: 4px 10px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">🏷️ ${tag}</span>
                            `).join('')}
                        </div>
                    ` : ''}
                    
                    ${quote.executiveNote ? `
                        <div class="executive-note">
                            <div class="note-label">📋 Your Reflection:</div>
                            <div class="note-text">${quote.executiveNote}</div>
                        </div>
                    ` : `
                        <button class="add-note-btn" data-quote-id="${quote.id}">
                            ✍️ Add Reflection
                        </button>
                    `}
                </div>
            `;
        }).join('');

        return `
            <div class="quotes-grid">
                ${quotesHTML}
            </div>
        `;
    }

    loadLibraryCSS() {
        console.log('🎨 Loading Library CSS...');
        
        // Load desktop template CSS
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css';
            link.onload = () => console.log('✅ Desktop template CSS loaded');
            link.onerror = () => console.warn('⚠️ Desktop template CSS failed - using critical CSS fallback');
            document.head.appendChild(link);
        }
        
        // Add Library-specific content styling
        this.addLibraryContentCSS();
        
        console.log('🎨 All Library CSS loaded');
    }

    addLibraryContentCSS() {
        const contentStyle = document.createElement('style');
        contentStyle.id = 'library-content-css';
        contentStyle.textContent = `
            /* Library Content Styling - Matches Analytics/Hall of Fame Theme */
            
            /* Header styling */
            #auth-menu-dom-overlay .header {
                height: 72px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                padding: 0 32px !important;
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(10px) !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                margin: 0 !important;
            }
            
            /* Filter tabs */
            .filter-tabs {
                display: flex !important;
                gap: 8px !important;
            }
            
            .filter-tab {
                padding: 8px 16px !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 8px !important;
                color: rgba(255, 255, 255, 0.7) !important;
                font-size: 14px !important;
                font-weight: 500 !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
            }
            
            .filter-tab:hover {
                background: rgba(255, 255, 255, 0.1) !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            .filter-tab.active {
                background: linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(14, 165, 233, 0.2)) !important;
                border-color: #00d4ff !important;
                color: #00d4ff !important;
            }
            
            /* Dashboard content - SCROLLABLE */
            #auth-menu-dom-overlay .dashboard-content {
                flex: 1 !important;
                padding: 24px 32px !important;
                overflow-y: auto !important;
                overflow-x: hidden !important;
                height: calc(100vh - 72px) !important;
            }
            
            /* Quotes grid */
            .quotes-grid {
                display: grid !important;
                grid-template-columns: repeat(auto-fill, minmax(400px, 1fr)) !important;
                gap: 24px !important;
            }
            
            /* Quote card */
            .quote-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 16px !important;
                padding: 24px !important;
                transition: all 0.3s ease !important;
                position: relative !important;
            }
            
            .quote-card:hover {
                transform: translateY(-4px) !important;
                border-color: rgba(0, 212, 255, 0.3) !important;
                box-shadow: 0 12px 40px rgba(0, 212, 255, 0.15) !important;
            }
            
            .quote-header {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-start !important;
                margin-bottom: 16px !important;
            }
            
            .quote-meta {
                display: flex !important;
                flex-direction: column !important;
                gap: 8px !important;
            }
            
            .quote-date {
                font-size: 12px !important;
                color: rgba(255, 255, 255, 0.6) !important;
            }
            
            .quote-emotion {
                font-size: 12px !important;
                background: rgba(0, 212, 255, 0.1) !important;
                padding: 4px 8px !important;
                border-radius: 6px !important;
                color: #00d4ff !important;
            }
            
            .favorite-btn {
                background: none !important;
                border: none !important;
                font-size: 24px !important;
                cursor: pointer !important;
                padding: 4px !important;
                opacity: 0.6 !important;
                transition: all 0.3s ease !important;
            }
            
            .favorite-btn:hover {
                opacity: 1 !important;
                transform: scale(1.2) !important;
            }
            
            .favorite-btn.active {
                opacity: 1 !important;
            }
            
            .quote-text {
                font-size: 18px !important;
                line-height: 1.6 !important;
                font-style: italic !important;
                color: rgba(255, 255, 255, 0.95) !important;
                margin-bottom: 16px !important;
                font-weight: 500 !important;
            }
            
            .quote-author {
                font-size: 16px !important;
                font-weight: 600 !important;
                color: #00d4ff !important;
                margin-bottom: 12px !important;
            }
            
            .quote-reasoning {
                font-size: 14px !important;
                line-height: 1.5 !important;
                color: rgba(255, 255, 255, 0.7) !important;
                padding: 12px !important;
                background: rgba(0, 212, 255, 0.05) !important;
                border-left: 3px solid #00d4ff !important;
                border-radius: 8px !important;
                margin-bottom: 12px !important;
            }
            
            .executive-note {
                background: rgba(168, 85, 247, 0.1) !important;
                border: 1px solid rgba(168, 85, 247, 0.3) !important;
                border-radius: 12px !important;
                padding: 16px !important;
                margin-top: 16px !important;
            }
            
            .note-label {
                font-size: 12px !important;
                font-weight: 600 !important;
                color: #a855f7 !important;
                margin-bottom: 8px !important;
            }
            
            .note-text {
                font-size: 14px !important;
                line-height: 1.5 !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            .add-note-btn {
                width: 100% !important;
                padding: 12px !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px dashed rgba(255, 255, 255, 0.2) !important;
                border-radius: 8px !important;
                color: rgba(255, 255, 255, 0.7) !important;
                font-size: 14px !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
                margin-top: 16px !important;
            }
            
            .add-note-btn:hover {
                background: rgba(255, 255, 255, 0.1) !important;
                border-color: #00d4ff !important;
                color: #00d4ff !important;
            }
            
            /* Empty state */
            .empty-state {
                text-align: center !important;
                padding: 80px 32px !important;
            }
            
            .empty-state-icon {
                font-size: 80px !important;
                margin-bottom: 24px !important;
            }
            
            .empty-state-title {
                font-size: 28px !important;
                font-weight: 700 !important;
                color: rgba(255, 255, 255, 0.9) !important;
                margin-bottom: 16px !important;
            }
            
            .empty-state-description {
                font-size: 16px !important;
                color: rgba(255, 255, 255, 0.7) !important;
                line-height: 1.6 !important;
                max-width: 500px !important;
                margin: 0 auto !important;
            }
        `;
        document.head.appendChild(contentStyle);
        console.log('🎨 Library content CSS added');
    }

    setupEventListeners() {
        // Filter tabs
        const filterTabs = document.querySelectorAll('.filter-tab');
        filterTabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                const filter = e.currentTarget.dataset.filter;
                this.filterQuotes(filter);
            });
        });
        
        // Tag filter dropdown toggle
        const tagFilterBtn = document.getElementById('tagFilterBtn');
        const tagFilterDropdown = document.getElementById('tagFilterDropdown');
        if (tagFilterBtn && tagFilterDropdown) {
            tagFilterBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const isVisible = tagFilterDropdown.style.display === 'block';
                tagFilterDropdown.style.display = isVisible ? 'none' : 'block';
            });
            
            // Close dropdown when clicking outside
            document.addEventListener('click', (e) => {
                if (!e.target.closest('.tag-filter-dropdown')) {
                    tagFilterDropdown.style.display = 'none';
                }
            });
        }
        
        // Tag filter options
        const tagFilterOptions = document.querySelectorAll('.tag-filter-option');
        tagFilterOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                const tag = e.currentTarget.dataset.tag;
                this.filterByTag(tag);
            });
        });
        
        // Favorite buttons
        const favoriteBtns = document.querySelectorAll('.favorite-btn');
        favoriteBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const quoteId = e.currentTarget.dataset.quoteId;
                this.toggleFavorite(quoteId);
            });
        });
        
        // Add note buttons
        const addNoteBtns = document.querySelectorAll('.add-note-btn');
        addNoteBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const quoteId = e.currentTarget.dataset.quoteId;
                this.showAddNoteDialog(quoteId);
            });
        });
        
        console.log('📚 Library event listeners setup complete');
    }
    
    filterByTag(tag) {
        console.log(`📚 Filtering by tag: ${tag}`);
        
        // Filter quotes to only show those with this tag
        const filteredQuotes = this.savedQuotes.filter(q => q.tags && q.tags.includes(tag));
        
        // Update the quotes display
        const quotesContainer = document.querySelector('.quotes-grid');
        if (quotesContainer) {
            if (filteredQuotes.length === 0) {
                quotesContainer.innerHTML = `
                    <div class="empty-state" style="grid-column: 1 / -1;">
                        <div class="empty-state-icon">🏷️</div>
                        <h2 class="empty-state-title">No Quotes with "${tag}"</h2>
                        <p class="empty-state-description">Try a different tag or view all quotes.</p>
                    </div>
                `;
            } else {
                quotesContainer.innerHTML = this.createQuoteCardsHTML(filteredQuotes);
            }
        }
        
        // Update filter button text
        const tagFilterBtn = document.getElementById('tagFilterBtn');
        if (tagFilterBtn) {
            tagFilterBtn.innerHTML = `
                🏷️ ${tag}
                <span style="opacity: 0.7; cursor: pointer;" onclick="event.stopPropagation(); window.currentLibraryScene?.clearTagFilter()">×</span>
            `;
        }
        
        // Close dropdown
        const tagFilterDropdown = document.getElementById('tagFilterDropdown');
        if (tagFilterDropdown) tagFilterDropdown.style.display = 'none';
        
        // Store reference
        window.currentLibraryScene = this;
    }
    
    clearTagFilter() {
        console.log('📚 Clearing tag filter');
        
        // Show all quotes again
        const quotesContainer = document.querySelector('.quotes-grid');
        if (quotesContainer) {
            quotesContainer.innerHTML = this.createQuoteCardsHTML(this.savedQuotes);
        }
        
        // Reset button text
        const tagFilterBtn = document.getElementById('tagFilterBtn');
        if (tagFilterBtn) {
            tagFilterBtn.innerHTML = `
                🏷️ Filter by Tag
                <span style="opacity: 0.7;">▼</span>
            `;
        }
        
        // Rebind events
        this.setupEventListeners();
    }
    
    createQuoteCardsHTML(quotes) {
        return quotes.map((quote, index) => {
            const dateStr = quote.createdAt ? quote.createdAt.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            }) : 'Recently';
            
            const favoriteIcon = quote.favorited ? '⭐' : '☆';
            
            return `
                <div class="quote-card" data-quote-id="${quote.id}">
                    <div class="quote-header">
                        <div class="quote-meta">
                            <span class="quote-date">📅 ${dateStr}${quote.createdAt ? ` at ${quote.createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                        </div>
                        <button class="favorite-btn ${quote.favorited ? 'active' : ''}" data-quote-id="${quote.id}">
                            ${favoriteIcon}
                        </button>
                    </div>
                    
                    <!-- Session Context from Philosophy Flow -->
                    ${(quote.selectedCategory || quote.selectedEmotion || quote.selectedColor || quote.selectedSleep) ? `
                        <div class="quote-metadata" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; padding: 12px; background: rgba(255, 255, 255, 0.02); border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.05);">
                            <div style="font-size: 11px; color: rgba(255, 255, 255, 0.5); width: 100%; margin-bottom: 6px; font-weight: 600;">Session Context:</div>
                            ${quote.selectedCategory ? `
                                <span class="meta-badge" style="
                                    background: rgba(16, 185, 129, 0.15);
                                    border: 1px solid rgba(16, 185, 129, 0.3);
                                    color: #10b981;
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">${quote.selectedCategory.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            ` : ''}
                            ${quote.selectedEmotion && quote.selectedEmotion.emoji && quote.selectedEmotion.label ? `
                                <span class="meta-badge" style="
                                    background: rgba(0, 212, 255, 0.15);
                                    border: 1px solid rgba(0, 212, 255, 0.3);
                                    color: #00d4ff;
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">${quote.selectedEmotion.emoji} ${quote.selectedEmotion.label}</span>
                            ` : ''}
                            ${quote.selectedColor && quote.selectedColor.name ? `
                                <span class="meta-badge" style="
                                    background: ${quote.selectedColor.hex}22;
                                    border: 1px solid ${quote.selectedColor.hex}66;
                                    color: ${quote.selectedColor.hex};
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">${quote.selectedColor.name}</span>
                            ` : ''}
                            ${quote.selectedSleep ? `
                                <span class="meta-badge" style="
                                    background: rgba(139, 92, 246, 0.15);
                                    border: 1px solid rgba(139, 92, 246, 0.3);
                                    color: #8b5cf6;
                                    padding: 6px 12px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">💤 ${quote.selectedSleep.hours || quote.selectedSleep} hrs</span>
                            ` : ''}
                        </div>
                    ` : ''}
                    
                    <div class="quote-text">
                        "${quote.quote || 'No quote text'}"
                    </div>
                    
                    <div class="quote-author">
                        — ${quote.figure || quote.author || 'Unknown'}
                    </div>
                    
                    ${quote.reasoning || quote.context ? `
                        <div class="context-section" style="margin-top: 16px;">
                            <button class="context-toggle-btn" data-quote-id="${quote.id}" onclick="this.classList.toggle('expanded'); const content = this.nextElementSibling; const arrow = this.querySelector('.toggle-arrow'); if(content.style.display === 'none' || !content.style.display) { content.style.display = 'block'; arrow.textContent = '▲'; } else { content.style.display = 'none'; arrow.textContent = '▼'; }" style="
                                width: 100%;
                                text-align: left;
                                background: rgba(0, 212, 255, 0.05);
                                border: 1px solid rgba(0, 212, 255, 0.2);
                                border-radius: 8px;
                                padding: 10px 12px;
                                color: #00d4ff;
                                font-size: 13px;
                                font-weight: 600;
                                cursor: pointer;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                transition: all 0.3s ease;
                            " onmouseover="this.style.background='rgba(0, 212, 255, 0.1)'; this.style.borderColor='rgba(0, 212, 255, 0.4)'" onmouseout="this.style.background='rgba(0, 212, 255, 0.05)'; this.style.borderColor='rgba(0, 212, 255, 0.2)'">
                                <span>📖 View Context & Reasoning</span>
                                <span class="toggle-arrow">▼</span>
                            </button>
                            <div class="context-content" style="
                                display: none;
                                margin-top: 8px;
                                padding: 12px;
                                background: rgba(0, 212, 255, 0.05);
                                border-left: 3px solid #00d4ff;
                                border-radius: 8px;
                                font-size: 14px;
                                line-height: 1.6;
                                color: rgba(255, 255, 255, 0.8);
                            ">
                                ${quote.reasoning || quote.context}
                            </div>
                        </div>
                    ` : ''}
                    
                    ${quote.tags && quote.tags.length > 0 ? `
                        <div class="quote-tags" style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px;">
                            ${quote.tags.map(tag => `
                                <span class="tag-badge" style="
                                    background: rgba(168, 85, 247, 0.15);
                                    border: 1px solid rgba(168, 85, 247, 0.3);
                                    color: #a855f7;
                                    padding: 4px 10px;
                                    border-radius: 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                ">🏷️ ${tag}</span>
                            `).join('')}
                        </div>
                    ` : ''}
                    
                    ${quote.executiveNote ? `
                        <div class="executive-note">
                            <div class="note-label">📋 Your Reflection:</div>
                            <div class="note-text">${quote.executiveNote}</div>
                        </div>
                    ` : `
                        <button class="add-note-btn" data-quote-id="${quote.id}">
                            ✍️ Add Reflection
                        </button>
                    `}
                </div>
            `;
        }).join('');
    }

    filterQuotes(filter) {
        this.currentFilter = filter;
        
        // Update active tab
        document.querySelectorAll('.filter-tab').forEach(tab => {
            tab.classList.remove('active');
            if (tab.dataset.filter === filter) {
                tab.classList.add('active');
            }
        });
        
        // Filter logic will be implemented when needed
        console.log(`📚 Filtering quotes by: ${filter}`);
    }

    async toggleFavorite(quoteId) {
        try {
            const quote = this.savedQuotes.find(q => q.id === quoteId);
            if (!quote) return;
            
            const newFavoriteState = !quote.favorited;
            
            // Update Firebase
            await db.collection('philosophyQuotes').doc(quoteId).update({
                favorited: newFavoriteState
            });
            
            // Update local state
            quote.favorited = newFavoriteState;
            
            // Update UI
            const btn = document.querySelector(`.favorite-btn[data-quote-id="${quoteId}"]`);
            if (btn) {
                btn.textContent = newFavoriteState ? '⭐' : '☆';
                if (newFavoriteState) {
                    btn.classList.add('active');
                } else {
                    btn.classList.remove('active');
                }
            }
            
            console.log(`📚 ${newFavoriteState ? 'Favorited' : 'Unfavorited'} quote: ${quoteId}`);
            
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    }

    showAddNoteDialog(quoteId) {
        // Simple prompt for now - can be enhanced later
        const note = prompt('Add your executive reflection:');
        if (note && note.trim()) {
            this.saveNote(quoteId, note.trim());
        }
    }

    async saveNote(quoteId, noteText) {
        try {
            // Update Firebase
            await db.collection('philosophyQuotes').doc(quoteId).update({
                executiveNote: noteText,
                lastUpdated: firebase.firestore.FieldValue.serverTimestamp()
            });
            
            // Update local state
            const quote = this.savedQuotes.find(q => q.id === quoteId);
            if (quote) {
                quote.executiveNote = noteText;
            }
            
            // Recreate interface to show updated note
            this.createLibraryInterface();
            this.setupEventListeners();
            
            console.log(`📚 Note saved for quote: ${quoteId}`);
            
        } catch (error) {
            console.error('Error saving note:', error);
            alert('Error saving note. Please try again.');
        }
    }

    shutdown() {
        console.log('[MyLibrarySceneDOM] Shutting down...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        super.shutdown();
    }
}

